import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    Injector,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    CMPS_DIALOG_DATA,
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
    CmpsSnackBar,
    CmpsTextOnlySnackBarComponent,
    CmpsTextOnlySnackBarOptions,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, timer } from 'rxjs';
import { debounceTime, skip, tap } from 'rxjs/operators';

import { AuthService } from '../../../../auth/auth.service';
import { FocusDto } from '../../../../coaching-base/general';
import { CoachingSwitchboardService } from '../../../../coaching-base/switchboard';
import { getDialogItems } from '../../../../coaching-commitment/functions/get-dialog-items.function';
import { goToDraftOrCommitment } from '../../../../coaching-commitment/functions/go-to-draft-or-commitment.function';
import { prepStatuses } from '../../../../coaching-commitment/functions/prep-statuses.function';
import { TabRefId } from '../../../../coaching-detail';
import { createNoteDetail } from '../../../../coaching-note/note-detail.form';
import { NotesComponent } from '../../../../coaching-note/notes.component';
import { CoachingObservationDialogService } from '../../../../coaching-observation/coaching-observation-dialog.service';
import { NavigationScenario } from '../../../../coaching-observation/observation/observation.model';
import { TagsDialogResult } from '../../../../coaching-tags/tags-dialog-result.model';
import { TagsComponent } from '../../../../coaching-tags/tags/tags.component';
import {
    FocusShallow,
    TagsDialogData,
    TagSetMode,
} from '../../../../coaching-tags/tags/tags.model';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../../common/take-until-destroy.decorator';
import { useDispatcher, UseStore } from '../../../../store.decorator';
import { ChatMember } from '../../../../teams-call';
import { useSelector2 } from '../../../store-use-selector';
import { copyTextToClipboard } from '../../../transcript-dialog/function/copy-text-to-clipboard.function';
import { ChatBubble } from '../model/chat-bubble.model';
import { updateChat } from '../state/update-chat.action';

import { createObservationOverride } from './function/create-observation-override.function';
import { ChatTranscriptDialogData } from './model/chat-transcript-dialog-data.model';
import { addCommitmentNotes } from './state/add-commitment-notes.action';
import { editNotesFocus } from './state/edit-notes-focus.action';
import { filterQuotes } from './state/filter-quotes.action';
import { reload } from './state/reload.action';
import { resetNav } from './state/reset-nav.action';
import { selectQuotes } from './state/select-quotes.action';
import { TranslationKeyMap } from './teamschat-transcript-dialog.i18n';
import { teamschatTranscriptDialogReducer } from './teamschat-transcript-dialog.reducer';
import { TeamschatTranscriptDialogState } from './teamschat-transcript-dialog.state';

@Component({
    selector: 'cmps-teamschat-transcript-dialog',
    templateUrl: './teamschat-transcript-dialog.component.html',
    host: {
        class: 'cmps-teamschat-transcript-dialog',
    },
})
@TakeUntilDestroy()
@UseStore(
    'coachingDetail.teamschatTranscriptDialog',
    teamschatTranscriptDialogReducer,
)
export class TeamschatTranscriptsDialogBoxComponent
    implements OnInit, AfterViewInit, TakeUntilDestroy
{
    state: TeamschatTranscriptDialogState;

    @ViewChild('commitment', { read: NotesComponent })
    commitmentNotes: NotesComponent;

    @ViewChild('observation', { read: NotesComponent })
    observationNotes: NotesComponent;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _form: FormGroup;
    /** @private */ _formHasNotes: boolean;
    /** @private */ _validFocusWarning: boolean;

    private _keywordSubj: Subject<string>;
    private _keywordSub: Subscription;

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _formBuilder: FormBuilder,
        private readonly _translateService: TranslateService,
        /** @private */ readonly _dialogRef: CmpsDialogRef<
            TeamschatTranscriptsDialogBoxComponent,
            void
        >,
        /** @private */ @Inject(CMPS_DIALOG_DATA)
        readonly _data: ChatTranscriptDialogData,
        /** @private */ readonly _observationDialogService: CoachingObservationDialogService,
        private readonly _dialog: CmpsDialog,
        private readonly _switchboardService: CoachingSwitchboardService,
        private readonly _snackBar: CmpsSnackBar,
        private readonly _injector: Injector,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _router: Router,
        private readonly _authService: AuthService,
    ) {
        this._formHasNotes = false;
        this._form = this._formBuilder.group({
            commitmentNotes: [],
            observationNotes: [],
        });
    }

    ngOnInit(): void {
        this._keywordSubj = new Subject();
        this._keywordSub = this._keywordSubj
            .asObservable()
            .pipe(
                debounceTime(500),
                tap((keyword) => {
                    useDispatcher(
                        this,
                        filterQuotes({
                            opts: { ...this.state.filterOpts, keyword },
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useSelector2<boolean>(
            (state) =>
                (
                    state?.coachingDetail
                        ?.transcriptDialog as TeamschatTranscriptDialogState
                )?.pending?.updateTranscript,
        )
            .pipe(
                skip(1),
                tap(([prevState, state]) => {
                    if (
                        prevState != null &&
                        state != null &&
                        prevState !== state &&
                        !state
                    ) {
                        this._transcriptUpdated();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useDispatcher(
            this,
            reload({
                chat: this._data.chat,
                messages: this._data.messages,
                behaviourId: this._data.context.behaviourId,
                subBehaviourId: this._data.context.subBehaviourId,
            }),
        );

        // reload has no side effect that _refocus() is waiting on, so this can be called here
        this._refocus();
    }

    ngAfterViewInit(): void {
        this._form.valueChanges.subscribe((change) => {
            if (
                change?.commitmentNotes?.length === 0 &&
                change?.observationNotes?.length === 0
            ) {
                this._formHasNotes = false;
            }
        });
    }

    ngOnDestroy(): void {
        this._keywordSub?.unsubscribe();
        this._keywordSubj?.complete();
    }

    stateToProps(prevState?: TeamschatTranscriptDialogState): void {
        if (
            !this.state.willNavigate &&
            (this.state.navigateCmtId != null ||
                this.state.navigateDraftId != null)
        ) {
            this._onRemoveAllNotesClick();
            this._switchboardService.commitment.sbReload(
                this.state.navigateCmtId,
                this.state.navigateDraftId,
            );
            void this._switchboardService.pillarTwo.sbReload(
                TabRefId.COMMITMENTS,
            );
            useDispatcher(this, resetNav({}));
        }
        if (this.state.willNavigate) {
            this._onRemoveAllNotesClick();
            void this._switchboardService.pillarTwo.sbReload(
                TabRefId.COMMITMENTS,
            );
            const dialogItems = getDialogItems(
                this._form.value,
                false,
                this.state.behaviours,
                this.state.subBehaviours,
                this._switchboardService,
            );
            const { statuses, promiseStatuses } = prepStatuses(
                false,
                dialogItems,
                this._switchboardService,
                null,
            );
            goToDraftOrCommitment(
                this.state.navigateDraftId,
                this.state.navigateCmtId,
                this._router,
                this._activatedRoute,
                this._switchboardService,
                statuses,
                promiseStatuses,
                this._data.context.performerId,
            );
            useDispatcher(this, resetNav({}));
            this._data.willNavigate?.();
            this._dialogRef.close();
        }
    }

    /** @private */
    _onFilterParticipantClick(member: ChatMember | null): void {
        const members = member == null ? null : [member];

        useDispatcher(
            this,
            filterQuotes({
                opts: {
                    ...this.state.filterOpts,
                    members,
                },
            }),
        );
    }

    /** @private */
    _onKeywordFilterChange(value: string): void {
        this._keywordSubj.next(value);
    }

    /** @private */
    _onSelectAllQuotesClick(): void {
        useDispatcher(
            this,
            selectQuotes({
                quotes: this.state.filteredQuotes,
                mode: 'toggle-all',
            }),
        );
    }

    /** @private */
    _onQuoteCheckboxChange(value: ChatBubble): void {
        useDispatcher(this, selectQuotes({ quotes: [value] }));
    }

    /** @private */
    _onCopyToClipboardClick(): void {
        const text = (this.state?.selectedQuotes ?? []).reduce((p, c) => {
            return `${p}${c.message ?? ''}\r\n`;
        }, '');
        copyTextToClipboard(text);
    }

    /** @private */
    _onPublishClick(): void {
        if (!this._validFocus()) {
            return;
        }

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._11033_WOULD_YOU_LIKE_TO_PUBLISH_THESE_COMMITMENT_NOTES_AND_CONTINUE,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._11039_PUBLISH_AND_CONTINUE,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._11040_PUBLISH_AND_CLOSE,
                    ),
                    data: 1,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._121_CANCEL,
                    ),
                    data: 2,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 2) {
                        return;
                    }
                    useDispatcher(
                        this,
                        addCommitmentNotes({
                            performerId: this._data.context.performerId,
                            coachId: this._data.context.coachId,
                            notes: this._form.value.commitmentNotes,
                            willNavigate: ret === 1,
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onOpenNewObservationClick(): void {
        const transcriptFocus = this.state.focus;
        const focus: FocusShallow = {
            behaviourId: transcriptFocus?.behaviour.id,
            subBehaviourId: transcriptFocus?.subBehaviour.id,
        };

        const observation = createObservationOverride(
            this.state?.chat,
            this.state.messages,
            this.state.focus,
            this._form.value.observationNotes,
        );

        let scenario: NavigationScenario;
        if (!this._switchboardService.commitment.connected()) {
            scenario =
                focus.behaviourId != null
                    ? NavigationScenario.POP_UP__FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_NO_COMMITMENT_LOADED
                    : NavigationScenario.POP_UP__NO_FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_NO_COMMITMENT_LOADED;
        } else {
            scenario =
                focus.behaviourId != null
                    ? NavigationScenario.POP_UP__FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_HAS_COMMITMENT_LOADED
                    : NavigationScenario.POP_UP__NO_FOCUS_SUPPLIED__NEWLY_PUBLISHED__PILLAR_3_HAS_COMMITMENT_LOADED;
        }

        let doNotNavigate: boolean = null;
        this._observationDialogService.openNew({
            focus,
            extraOptions: {
                observation,
                scenario,
                canChangeActiveFocus: true,
                canDeactivateWithoutConfirm: true,
                performerId: this._data.context.performerId,
                willPublish: (opts: { doNotNavigate: boolean }) => {
                    doNotNavigate = opts.doNotNavigate;
                },
                doNavigate: () => doNotNavigate,
                onNavigate: () => {
                    if (!doNotNavigate) {
                        this._switchboardService.observationReview.sbWillNavigate();
                        this._switchboardService.commitment.sbWillNavigate();
                        return;
                    }

                    this._onResetClick();

                    // ideally this should be wrapped in a useSelector()
                    this._onRemoveAllNotesClick();
                },
            },
        });
    }

    /** @private */
    _onResetClick(): void {
        useDispatcher(
            this,
            reload({
                chat: this._data.chat,
                messages: this._data.messages,
                behaviourId: this._data.context.behaviourId,
                subBehaviourId: this._data.context.subBehaviourId,
            }),
        );
    }

    /** @private */
    _onCreateNewNoteClick(isCommitmentNote: boolean): void {
        this._formHasNotes = true;
        const text = (this.state?.selectedQuotes ?? []).reduce((p, c) => {
            return `${p}${c.message ?? ''}<br/>`;
        }, '');
        const note = createNoteDetail(this.state?.defaultNoteCategory);
        note.contents = text;
        note.noteId = 0;
        note.createdByFirstName = this._authService.user.firstName;
        note.createdByLastName = this._authService.user.lastName;
        note.updatedByFirstName = this._authService.user.firstName;
        note.updatedByLastName = this._authService.user.lastName;
        isCommitmentNote
            ? this.commitmentNotes.addNote(note)
            : this.observationNotes.addNote(note);

        useDispatcher(this, selectQuotes({ quotes: [], mode: 'set' }));
    }

    /** @private */
    _onRemoveAllNotesClick(): void {
        this._form?.patchValue({ commitmentNotes: [], observationNotes: [] });
        this._formHasNotes = false;
    }

    /** @private */
    _onTopicChange(value: string): void {
        if (this.state?.chat?.topic === value) {
            return;
        }
        const chat = {
            ...this.state.chat,
            topic: value,
        };
        useDispatcher(
            this,
            updateChat({
                chat,
            }),
        );
        this._onResetClick();
    }

    /** @private */
    _onNotesEditFocusClick(): void {
        const data = this._onEditFocus();
        const focusDialogRef: CmpsDialogRef<TagsComponent, TagsDialogResult> =
            this._dialog.open(TagsComponent, {
                injector: this._injector,
                data,
            });
        focusDialogRef.result
            .pipe(
                tap((ret) => {
                    if (ret == null) {
                        return;
                    }

                    useDispatcher(
                        this,
                        editNotesFocus({
                            behaviourId: ret.selected.behaviourId,
                            subBehaviourId: ret.selected.subBehaviourId,
                        }),
                    );

                    // TODO revise. emulate form validation. cmps-tag-set-trigger and formControlName may be used in the future
                    this._validFocus();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onDateRangeFilterChange(dateRange: { from: Date; to: Date }): void {
        useDispatcher(
            this,
            filterQuotes({
                opts: {
                    ...this.state.filterOpts,
                    dateRange: {
                        from: dateRange.from,
                        to: dateRange.to,
                    },
                },
            }),
        );
    }

    /** cmps-dialog has no option to set the initial element when doing focus-trap.
     * the title element is the first element, but it should not have focus.
     * enabled after the component instantiation task cycle */
    private _refocus(): void {
        if (this.state?.allowedActions?.editTranscript !== true) {
            return;
        }
        timer(1)
            .pipe(
                tap(() => {
                    this._el.nativeElement
                        .querySelector('input.title')
                        ?.removeAttribute('disabled');
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _transcriptUpdated(): void {
        const data: CmpsTextOnlySnackBarOptions = {
            text: this._translateService.instant(
                TranslationKeyMap._10056_TRANSCRIPT_SAVED,
            ),
        };
        this._snackBar.open(CmpsTextOnlySnackBarComponent, {
            duration: 5000,
            placement: 'bottom-right',
            data,
        });
    }

    private _onEditFocus(): TagsDialogData {
        const chatFocus = this.state.focus;

        const selected: FocusDto = {
            userId: this._data.context.performerId,
            focusId: null,
            behaviourId: chatFocus?.behaviour.id,
            subBehaviourId: chatFocus?.subBehaviour.id,
            metrics: [],
        };

        // Todo rename store path
        const data: TagsDialogData = {
            storePathPrefix: 'coachingDetail.transcriptDialogN',
            mode: TagSetMode.FOCUS_ONLY,
            focusSelectionParams: null,
            commitmentId: null,
            performerId: 26, // performer.userId,
            selected,
            pinnedMetrics: null,
        };
        return data;
    }

    private _validFocus(): boolean {
        const result = this.state?.cmtNotesFocus?.behaviour != null;
        this._validFocusWarning = !result;
        return result;
    }
}
